import { useEffect } from 'react';
import { Button, List, ListItem, ListItemAvatar, Avatar, ListItemText, IconButton } from '@mui/material';
import { Delete as DeleteIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material';

import { useTrans } from '~/hooks/useTrans';
import { IUrlParams } from "~/interfaces";
import { IClientProject } from "~/providers/client-interfaces";
import { IAdminProject } from "~/providers/admin-interfaces";
import WalletConnectModal from "~/modals/WalletConnectModal";
import RemoveConnectedWallet from "~/modals/RemoveConnectedWallet";
import RemoveWalletInvitation from "~/modals/RemoveWalletInvitation";
import { myUseState } from "~/hooks/myUseState";
import { apiGetProjectWallet, apiGetProjectWalletInvitation } from "~/providers/api";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IWalletItem {
	email: string;
	msg: string | null;
	isError: boolean;
	isInvitation: boolean;
}
interface IState {
	setWalletConnectSwitch: boolean;
	showWalletConnect: boolean;
	showWalletRemove: boolean;
	showWalletInvitationRemove: boolean;
	wasChanged: boolean;
	walletItems: Array<IWalletItem>;
	showWalletReconnect: boolean;
	dialogLoading: boolean;
	errorMsg: string;
}

interface IPaidTypeContent {
	params: IUrlParams;
	isInternal: boolean;
	isVisitor: boolean;
	projectData: IClientProject | IAdminProject;
	onSave?: (lastUpdate: string) => void;
	onChange?: (isChanged: boolean) => void;
}

export default function PaidTypeContent({
	params,
	isInternal = false,
	isVisitor = false,
	projectData,
	onSave = () => {},
	onChange = () => {},
}: IPaidTypeContent) {
	const { lang } = useLang();
	const { trans, transBillingMethod, formatPennyPrice } = useTrans();
	const { state, updateState } = myUseState<IState>({
		setWalletConnectSwitch: false,
		showWalletConnect: false,
		showWalletRemove: false,
		showWalletInvitationRemove: false,
		wasChanged: false,
		walletItems: [],
		showWalletReconnect: false,
		dialogLoading: false,
		errorMsg: "",
	});
	const showConnectOtherWallet = projectData.billingMethod === "wallet" && state.showWalletReconnect && !isInternal;
	const showConnectWallet = ((projectData.billingMethod === "wallet" && state.walletItems.length === 0) || projectData.billingMethod === "none") && !isInternal;

	async function loadData() {
		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const [walletData, walletInvitationData] = await Promise.all([
			apiGetProjectWallet(isInternal ? "admin" : "client", projectData.id, lang),
			apiGetProjectWalletInvitation(isInternal ? "admin" : "client", projectData.id, lang),
		]);
		const walletItems: Array<IWalletItem> = [];
		let showWalletReconnect = false;

		if (walletData.noErrors && walletData.data) {
			walletItems.push({
				email: walletData.data.email,
				msg: trans("paidTypeContent.actualAmount", { price: formatPennyPrice(walletData.data.amount) }),
				isInvitation: false,
				isError: false,
			});
			showWalletReconnect = true;
		}

		if (walletInvitationData.noErrors && walletInvitationData.data) {
			walletItems.push({
				email: walletInvitationData.data.requestedWalletEmail,
				msg: walletInvitationData.data.expired ? trans("paidTypeContent.expired") : trans("paidTypeContent.pending"),
				isInvitation: true,
				isError: walletInvitationData.data.expired,
			});
			showWalletReconnect = showWalletReconnect || walletInvitationData.data.expired;
		}

		updateState({
			dialogLoading: false,
			walletItems,
			showWalletReconnect,
			errorMsg: [
				...walletData.errors,
				...walletInvitationData.errors,
			].join(", "),
		});
	}

	// stejna fce pro oboje
	function onWalletSave(lastUpdate: string) {
		updateState({
			showWalletConnect: false,
			showWalletRemove: false,
			showWalletInvitationRemove: false,
			setWalletConnectSwitch: false,
			wasChanged: false,
		});
		onChange(false);
		onSave(lastUpdate);
		loadData();
	}

	function setWasChanged(wasChanged: boolean) {
		updateState({ wasChanged });
		onChange(wasChanged);
	}

	function removeItem(isPending: boolean) {
		updateState({
			showWalletRemove: isPending === false,
			showWalletInvitationRemove: isPending === true,
		});
	}

	function getTitle() {
		// chyba - excel Lasak
		if (projectData.billingMethod === "none" && state.walletItems.length > 0) {
			return trans("projectFinancialPage.paidType.wallet");
		}

		if (projectData.billingMethod === "sos") {
			return trans("paidTypeContent.sos");
		} else if (projectData.billingMethod === "barter") {
			return trans("paidTypeContent.barter");
		}

		return transBillingMethod(projectData.billingMethod);
	}

	useEffect(() => {
		loadData();
	}, [projectData]);

	return <div className="paidTypeContent">
		<p>
			{ getTitle() }
		</p>
		{ state.walletItems.length > 0 && <List sx={{ marginLeft: "calc(var(--margin-large) * -1)" }}>
			{ state.walletItems.map(walletItem => <ListItem
				secondaryAction={isVisitor || isInternal
					? null
					: <IconButton edge="end" aria-label="delete" onClick={() => removeItem(walletItem.isInvitation)}>
						<DeleteIcon />
					</IconButton>
				} key={walletItem.email}>
				<ListItemAvatar>
					<Avatar>
						<AccountCircleIcon />
					</Avatar>
				</ListItemAvatar>
				<ListItemText
					className="paidTypeContent__walletItem"
					primary={walletItem.email}
					secondary={walletItem.msg}
					primaryTypographyProps={{ ...walletItem.isError ? { color: "var(--red-text-color)" } : {} }}
					secondaryTypographyProps={{ ...walletItem.isError ? { color: "var(--red-text-color)" } : {} }}
				/>
			</ListItem>) }
		</List> }
		{ (showConnectOtherWallet || showConnectWallet) && <div className="paidTypeContent__walletBtns">
			{ showConnectOtherWallet && <Button onClick={() => updateState({ showWalletConnect: true, setWalletConnectSwitch: true })} color="success" variant="contained" disabled={isVisitor}>
				{ trans("paidTypeContent.connectOtherWallet") }
			</Button> }
			{ showConnectWallet && <Button onClick={() => updateState({ showWalletConnect: true, setWalletConnectSwitch: false })} color="success" variant="contained" disabled={isVisitor}>
				{ trans("projectFinancialPage.paidType.connectWallet") }
			</Button> }
		</div> }
		{ !projectData.billedConsumption && projectData.billingMethod !== "none" && <p className="paidTypeContent__errorInfo">
			{ trans("projectFinancialPage.paidType.paidNotSet") }
		</p>}
		{ state.showWalletConnect && <WalletConnectModal projectData={projectData} switchWallet={state.setWalletConnectSwitch} onSave={onWalletSave}
			onClose={() => updateState({ showWalletConnect: false, setWalletConnectSwitch: false, wasChanged: false })} onChange={wasChanged => setWasChanged(wasChanged)} /> }
		{ state.showWalletRemove && <RemoveConnectedWallet params={params} projectLastUpdate={projectData.lastUpdate} onClose={() => updateState({ showWalletRemove: false })}
			onSave={onWalletSave} /> }
		{ state.showWalletInvitationRemove && <RemoveWalletInvitation params={params} projectLastUpdate={projectData.lastUpdate} onClose={() => updateState({ showWalletInvitationRemove: false })}
			onSave={onWalletSave} /> }
	</div>;
}
